/**
 * HTTP layer for cellular billing. Invoices, billing accounts with their
 * agreements and notes, the device- and feature-pricing catalogs, prepaid
 * coverage rollups, and device installations. Backs the billing admin grids and
 * the per-account pricing-config editors.
 *
 * Contains:
 * - fetchInvoices / fetchInvoice / patchInvoice / markInvoicesComplete: invoice reads and status writes
 * - fetchBillingAccounts / fetchBillingAccount / generateAccountInvoices: account reads and invoice generation
 * - billing-account agreement and note CRUD
 * - device- and feature-pricing category/price CRUD plus per-account pricing-config batch save
 * - fetchPrepaidSummary / fetchPrepaidCoveragesGrid: prepaid coverage
 * - fetchInstallations / fetchBilledEntities: installation and billed-entity reads
 */
import apiClient from '@sophocles/http-common';

export const INVOICES_ENDPOINT = '/billing/invoices';
export const ACCOUNTS_ENDPOINT = '/billing/accounts';
export const BILLED_ENTITIES_ENDPOINT = '/billing/billed-entities';
export const DEVICE_CATEGORIES_ENDPOINT = '/billing/device-categories';
export const DEVICE_PRICING_ENDPOINT = '/billing/device-pricing';
export const FEATURE_CATEGORIES_ENDPOINT = '/billing/feature-categories';
export const FEATURE_PRICING_ENDPOINT = '/billing/feature-pricing';
export const ACCOUNT_NOTES_ENDPOINT = '/billing/account-notes';
export const ACCOUNT_AGREEMENTS_ENDPOINT = '/billing/account-agreements';
export const PREPAID_COVERAGES_ENDPOINT = '/billing/prepaid-coverages';
export const INSTALLATIONS_ENDPOINT = '/billing/installations';
// odl-app grid read for prepaid coverages — distinct base path from the legacy
// PREPAID_COVERAGES_ENDPOINT mutation surfaces above.
const PREPAID_COVERAGES_GRID_ENDPOINT = 'odl-app/prepaid-coverages/';

export const fetchInstallations = queryParams =>
  apiClient.get(`${INSTALLATIONS_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

export const fetchPrepaidCoveragesGrid = params =>
  apiClient.get(PREPAID_COVERAGES_GRID_ENDPOINT, { params }).then(res => res.data);

export const fetchInvoice = id => apiClient.get(`${INVOICES_ENDPOINT}/${id}/`).then(res => res.data);

export const patchInvoice = (id, data) => apiClient.patch(`${INVOICES_ENDPOINT}/${id}/`, data).then(res => res.data);

export const fetchInvoices = queryParams =>
  apiClient.get(`${INVOICES_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

export const markInvoicesComplete = invoiceIds =>
  apiClient.patch(`${INVOICES_ENDPOINT}/complete/`, invoiceIds).then(res => res.data);

export const fetchBillingAccounts = queryParams =>
  apiClient.get(`${ACCOUNTS_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

export const fetchBillingAccount = id => apiClient.get(`${ACCOUNTS_ENDPOINT}/${id}/`).then(res => res.data);

export const generateAccountInvoices = accountId =>
  apiClient.post(`${ACCOUNTS_ENDPOINT}/generate-invoices/`, { account_id: accountId }).then(res => res.data);

export const fetchBillingAccountAgreements = queryParams =>
  apiClient.get(`${ACCOUNT_AGREEMENTS_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

export const fetchBillingAccountAgreement = id =>
  apiClient.get(`${ACCOUNT_AGREEMENTS_ENDPOINT}/${id}/`).then(res => res.data);

export const postBillingAccountAgreement = data =>
  apiClient.post(`${ACCOUNT_AGREEMENTS_ENDPOINT}/`, data).then(res => res.data);

export const patchBillingAccountAgreement = (id, data) =>
  apiClient.patch(`${ACCOUNT_AGREEMENTS_ENDPOINT}/${id}/`, data).then(res => res.data);

export const postBillingAccountNote = data => apiClient.post(`${ACCOUNT_NOTES_ENDPOINT}/`, data).then(res => res.data);

export const deleteBillingAccountNote = id => apiClient.delete(`${ACCOUNT_NOTES_ENDPOINT}/${id}/`).then(res => id);

export const patchBillingAccountNote = (id, data) =>
  apiClient.patch(`${ACCOUNT_NOTES_ENDPOINT}/${id}/`, data).then(res => res.data);

export const fetchDeviceBillingCategories = queryParams =>
  apiClient.get(`${DEVICE_CATEGORIES_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

export const fetchDevicePricing = queryParams =>
  apiClient.get(`${DEVICE_PRICING_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

export const fetchFeaturePricing = queryParams =>
  apiClient.get(`${FEATURE_PRICING_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

export const postDevicePricing = data => apiClient.post(`${DEVICE_PRICING_ENDPOINT}/`, data).then(res => res.data);

export const deleteDevicePricing = id => apiClient.delete(`${DEVICE_PRICING_ENDPOINT}/${id}/`).then(res => id);

export const patchDevicePricing = (id, data) =>
  apiClient.patch(`${DEVICE_PRICING_ENDPOINT}/${id}/`, data).then(res => res.data);

export const patchFeaturePricing = (id, data) =>
  apiClient.patch(`${FEATURE_PRICING_ENDPOINT}/${id}/`, data).then(res => res.data);

export const postFeaturePricing = data => apiClient.post(`${FEATURE_PRICING_ENDPOINT}/`, data).then(res => res.data);

export const deleteFeaturePricing = id => apiClient.delete(`${FEATURE_PRICING_ENDPOINT}/${id}/`).then(res => id);

export const fetchPrepaidSummary = params =>
  apiClient.get(`${PREPAID_COVERAGES_ENDPOINT}/summary/`, { params }).then(res => res.data);

export const fetchBilledEntities = queryParams =>
  apiClient.get(`${BILLED_ENTITIES_ENDPOINT}/`, { params: queryParams }).then(res => res.data);

// Feature Pricing Config
export const fetchFeaturePricingConfig = accountId =>
  apiClient.get(`${ACCOUNTS_ENDPOINT}/${accountId}/feature-pricing-config/`).then(res => res.data);

export const saveFeaturePricingConfigBatch = (accountId, configs) =>
  apiClient.post(`${ACCOUNTS_ENDPOINT}/${accountId}/feature-pricing-config/batch/`, { configs }).then(res => res.data);

// Device Pricing Config
export const fetchDevicePricingConfig = accountId =>
  apiClient.get(`${ACCOUNTS_ENDPOINT}/${accountId}/device-pricing-config/`).then(res => res.data);

export const saveDevicePricingConfigBatch = (accountId, configs) =>
  apiClient.post(`${ACCOUNTS_ENDPOINT}/${accountId}/device-pricing-config/batch/`, { configs }).then(res => res.data);

// Default Pricing Templates (for inline mode without an account)
export const fetchDefaultDevicePricingTemplate = () =>
  apiClient.get(`${DEVICE_CATEGORIES_ENDPOINT}/pricing-template/`).then(res => res.data);

export const fetchDefaultFeaturePricingTemplate = () =>
  apiClient.get(`${FEATURE_CATEGORIES_ENDPOINT}/pricing-template/`).then(res => res.data);
